import { useSyncExternalStore } from "react";
import { resolveUiConfig, type ResolvedUiConfig } from "@repo/ui-config";

import { BASE_UI_CONFIG, SHARED_BASE_UI_CONFIG } from "./base-config";
import { readUiConfigOverlayLanguageLocale } from "./language-config";
import { readUiConfigOverlayNumberFormatMode } from "./number-format-config";
import {
  getUiConfigOverlay,
  subscribeToUiConfigOverlay,
} from "./overlay-store";

/**
 * Resolved (base ⊕ overlay) config, cached on overlay identity so
 * `useSyncExternalStore` consumers get a stable snapshot between changes.
 */

let cachedOverlay: unknown = null;
type AppResolvedUiConfig = Omit<ResolvedUiConfig, "lenses"> & {
  lenses: ResolvedUiConfig["lenses"] & {
    language: { locale: string };
    numberFormat: { mode: string };
  };
};

let cachedResolved: AppResolvedUiConfig | null = null;

export function getResolvedUiConfig(): AppResolvedUiConfig {
  const overlay = getUiConfigOverlay();
  if (cachedResolved === null || cachedOverlay !== overlay) {
    cachedOverlay = overlay;
    const sharedResolved = resolveUiConfig(SHARED_BASE_UI_CONFIG, overlay);
    cachedResolved = {
      ...sharedResolved,
      lenses: {
        ...sharedResolved.lenses,
        // Config-only base ⊕ overlay value; deliberately navigator-blind.
        // Read the effective language through getLanguageLocale() or
        // useLanguageLocale() from ~/lib/language-lens.
        language: {
          locale:
            readUiConfigOverlayLanguageLocale(overlay) ??
            BASE_UI_CONFIG.lenses.language.locale,
        },
        numberFormat: {
          mode:
            readUiConfigOverlayNumberFormatMode(overlay) ??
            BASE_UI_CONFIG.lenses.numberFormat.mode,
        },
      },
    };
  }
  return cachedResolved;
}

export function useResolvedUiConfig(): AppResolvedUiConfig {
  return useSyncExternalStore(
    subscribeToUiConfigOverlay,
    getResolvedUiConfig,
    getResolvedUiConfig,
  );
}

/**
 * Ordered, visible item ids for one configurable surface — the shape
 * region-rendering call sites want.
 */
export function useVisibleSurfaceItems(surfaceId: string): string[] {
  const resolved = useResolvedUiConfig();
  const surface = resolved.surfaces[surfaceId];
  return useSyncExternalStoreStableList(surface);
}

// Stable identity for the derived list: recompute only when the resolved
// surface object (itself cached on overlay identity) changes.
const surfaceListCache = new WeakMap<object, string[]>();

function useSyncExternalStoreStableList(
  surface: AppResolvedUiConfig["surfaces"][string] | undefined,
): string[] {
  if (surface === undefined) {
    return [];
  }
  const cached = surfaceListCache.get(surface);
  if (cached !== undefined) {
    return cached;
  }
  const list = surface.items
    .filter((item) => !item.hidden)
    .map((item) => item.id);
  surfaceListCache.set(surface, list);
  return list;
}
